/**
 * **One booking** — `/bookings/:bookingId` (C19, FR-J11 item 9, ADR-0037 §6;
 * Wave 19B, T-5.60). A `RecordPage`: the client, the booking's status and the
 * facts a reader needs first, then its parts as section cards — the answers
 * (labelled from the flow as it was when the visitor began, 19A note §3.3),
 * the service and plan, the invoice and its payment, and a timeline.
 *
 * **No longer read only** (C25, ADR-0052 §4; Wave 27B, T-5.74). On every
 * unconfirmed booking, gateway or bank QR:
 *
 * - **Confirm payment** is the primary action (`ConfirmPaymentDialog`): an
 *   invoice when there is none, a receipt for what is outstanding, the booking
 *   confirmed — step-up only when an invoice will be written;
 * - **Cancel booking** sits under *More*, through the kit's `ConfirmDialog` with
 *   a **required reason** (ADR-0033), which the client's email carries; a full
 *   credit note, behind step-up, when an unpaid invoice exists. A part-paid or
 *   confirmed booking says why it cannot be cancelled here.
 *
 * What the service allows is `row.allowed`; what the person may do is their
 * verbs here (`booking:can_update`, and `ledger:can_create` to record the
 * receipt) — the UI half of the service's own gates. A *Proof to check*
 * booking says so at the top, with the visitor's reference.
 */

import { Alert, Button, ConfirmDialog, type DropdownMenuItem, buttonClass, formatDateTime } from '@fiveox/ui'
import { CalendarClock, ClipboardList, IndianRupee, ListChecks } from 'lucide-react'
import { type ReactNode, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'

import { StepUpDialog } from '../../../components/StepUpDialog'
import { RecordPage, RecordSection, StatusBadge } from '../../../components/page'
import { BOOKING, LEDGER, useEvaluation } from '../../../lib/access'
import { ApiError } from '../../../lib/api'
import { type BookingAnswer, type BookingDetail, booking } from '../../../lib/api/booking'
import { Money } from '../../Billing/shared'
import { BookingDocuments } from './BookingDocuments'
import { ConfirmPaymentDialog } from './ConfirmPaymentDialog'

const back = { to: '/bookings', label: 'All bookings' }

/** An answer as the firm reads it. */
export function answerText(answer: BookingAnswer): string {
  const { value } = answer
  if (value === null || value === undefined || value === '') return '—'
  if (answer.kind === 'checkbox' || typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

/** The sentences the service says beside `allowed` (27B) — read loosely, so a
 *  service before them still draws. */
type Refusals = { confirm_refusal?: string | null; cancel_refusal?: string | null }

/** C25 — the two actions as the record offers them: what the service allows,
 *  narrowed by the verbs this person holds. Pure, so it is tested alone. */
export function bookingActions(
  row: Pick<BookingDetail, 'status' | 'allowed'>,
  can: (resource: string, verb: 'can_update' | 'can_create') => boolean,
): { confirm: boolean; cancel: boolean | null; cancelWhy: string | null; credits: boolean } {
  const allowed = row.allowed
  const updates = can(BOOKING, 'can_update')
  const refusals = (allowed ?? {}) as Refusals
  // A cancelled booking or a request has nothing to cancel and says nothing;
  // a confirmed one says why it is refunded in Billing instead.
  const offersCancel = updates && Boolean(allowed) && row.status !== 'cancelled' && row.status !== 'request'
  return {
    confirm: Boolean(allowed?.confirm_payment) && updates && can(LEDGER, 'can_create'),
    cancel: offersCancel ? Boolean(allowed?.cancel) : null,
    cancelWhy: allowed?.cancel ? null : (refusals.cancel_refusal ?? null),
    credits: Boolean(allowed?.cancel_credits_invoice),
  }
}

export function BookingRecord() {
  const { bookingId = '' } = useParams()
  const navigate = useNavigate()
  const { can } = useEvaluation()
  const [row, setRow] = useState<BookingDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [cancelReason, setCancelReason] = useState<string | null>(null)
  const [cancelError, setCancelError] = useState<string | null>(null)
  const [cancelBusy, setCancelBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    setRow(null)
    setError(null)
    booking
      .booking(bookingId)
      .then((body) => live && setRow(body.data))
      .catch((err: unknown) => {
        if (!live) return
        setError(
          err instanceof ApiError && err.status === 404
            ? 'This booking does not exist, or is not one of your firm’s.'
            : err instanceof ApiError
              ? err.message
              : 'This booking could not be loaded. Reload the page to try again.',
        )
      })
    return () => {
      live = false
    }
  }, [bookingId])

  if (!row) {
    return <RecordPage back={back} title="Booking" loading error={error} />
  }

  const link = (to: string, text: ReactNode) => (
    <Link to={to} className="text-primary font-medium hover:underline">
      {text}
    </Link>
  )

  const actions = bookingActions(row, (resource, verb) => can(resource, verb))
  const openInvoice = row.invoice_id ? `/billing/invoices/${row.invoice_id}` : null

  const closeCancel = () => {
    setCancelling(false)
    setCancelReason(null)
    setCancelError(null)
    setCancelBusy(false)
  }

  const cancel = async (reason: string, assertion?: string) => {
    setCancelBusy(true)
    setCancelError(null)
    try {
      const resp = await booking.cancel(row.id, { reason }, assertion)
      setRow(resp.data)
      setNotice('The booking is cancelled. The client is emailed the reason.')
      closeCancel()
    } catch (err) {
      setCancelError(err instanceof ApiError ? err.message : 'The booking could not be cancelled.')
      setCancelReason(null)
      setCancelBusy(false)
    }
  }

  const more: DropdownMenuItem[] = []
  if (actions.confirm && openInvoice) {
    more.push({ id: 'invoice', label: 'Open the invoice', onSelect: () => void navigate(openInvoice) })
  }
  if (actions.cancel !== null) {
    more.push({
      id: 'cancel',
      label: 'Cancel booking',
      tone: 'danger',
      disabled: !actions.cancel,
      description: actions.cancel
        ? actions.credits
          ? 'Its unpaid invoice is cancelled by a credit note.'
          : undefined
        : (actions.cancelWhy ?? undefined),
      separatorBefore: more.length > 0,
      onSelect: () => setCancelling(true),
    })
  }

  return (
    <RecordPage
      back={back}
      title={row.client_name ?? 'Booking'}
      status={{ kind: 'booking', status: row.status }}
      facts={[
        { label: 'Flow', value: row.flow_name ?? row.flow_slug },
        { label: 'Amount', value: <Money value={row.amount} /> },
        { label: 'Started', value: row.created_at ? formatDateTime(row.created_at) : '—' },
        ...(row.payment_reference ? [{ label: 'Reference', value: row.payment_reference }] : []),
      ]}
      primary={
        actions.confirm ? (
          <Button onClick={() => setConfirming(true)}>Confirm payment</Button>
        ) : openInvoice ? (
          <Link to={openInvoice} className={buttonClass({ variant: 'outline' })}>
            Open the invoice
          </Link>
        ) : undefined
      }
      more={more}
    >
      {notice && (
        <Alert tone="success">
          <span data-notice="">{notice}</span>
        </Alert>
      )}
      {row.status === 'payment_review' && (
        <Alert tone="warning">
          <span data-proof-to-check="">
            A payment proof is waiting for you
            {row.proof_submitted_at ? ` (sent ${formatDateTime(row.proof_submitted_at)})` : ''}. Check the screenshot
            {row.payment_reference ? ` and the reference ${row.payment_reference}` : ''} against your bank, then confirm the
            payment or cancel the booking.
          </span>
        </Alert>
      )}
      {row.status === 'cancelled' && row.cancel_reason && (
        <Alert tone="info">
          <span data-cancel-reason="">Cancelled: {row.cancel_reason}</span>
        </Alert>
      )}
      {row.last_error && (
        <Alert tone="warning">
          <span data-last-error="">{row.last_error}</span>
        </Alert>
      )}

      {confirming && (
        <ConfirmPaymentDialog
          row={row}
          onClose={() => setConfirming(false)}
          onConfirmed={(next) => {
            setRow(next)
            setConfirming(false)
            setNotice('Payment confirmed. The client is emailed the confirmation and the receipt.')
          }}
        />
      )}
      <ConfirmDialog
        open={cancelling && cancelReason === null}
        onClose={closeCancel}
        title="Cancel this booking?"
        tone="danger"
        confirmLabel="Cancel booking"
        cancelLabel="Keep the booking"
        reason={{
          label: 'Reason',
          hint: 'The client is emailed this. It is not shown anywhere else outside your firm.',
          required: 'Say why the booking is cancelled — the client is told in the email.',
        }}
        busy={cancelBusy}
        error={cancelError}
        onConfirm={({ reason }) => {
          if (reason.length < 3) {
            setCancelError('Say why the booking is cancelled, in a few words.')
            return
          }
          if (actions.credits) setCancelReason(reason)
          else void cancel(reason)
        }}
      >
        <p>
          The booking is closed and the client is emailed your reason.
          {actions.credits
            ? ' Its unpaid invoice is cancelled by a full credit note, which takes the next number in your series.'
            : ' No invoice has been issued for it, so nothing is credited.'}
        </p>
        <p>
          {row.status === 'started'
            ? 'Files the visitor uploaded but never sent are deleted, as an abandoned booking’s are.'
            : 'Nothing the visitor sent is deleted: their documents stay on the client’s record.'}
        </p>
      </ConfirmDialog>
      <StepUpDialog
        open={cancelReason !== null}
        title="Cancel this booking"
        what="A credit note is issued against the booking's invoice and takes the next number in your series. It cannot be undone from here."
        onClose={closeCancel}
        onVerified={(assertion) => cancel(cancelReason ?? '', assertion)}
      />

      <div className="grid min-w-0 gap-6 lg:grid-cols-2">
        <RecordSection
          id="booking-client"
          icon={<ClipboardList size={18} aria-hidden />}
          title="Client and answers"
          description="Who booked, and what they told you."
        >
          <dl className="grid gap-x-4 gap-y-2 text-sm sm:grid-cols-[10rem_minmax(0,1fr)]" data-answers="">
            <dt className="text-muted-foreground">Client</dt>
            <dd className="min-w-0 break-words">
              {link(`/clients/${row.client_id}`, row.client_name ?? 'Open the client')}
            </dd>
            {row.answers.map((answer) => (
              <Pair key={answer.key} label={answer.label}>
                {answerText(answer)}
              </Pair>
            ))}
          </dl>
          {row.answers.length === 0 && (
            <p className="text-muted-foreground mt-2 text-sm">This flow asked no questions.</p>
          )}
        </RecordSection>

        <RecordSection
          id="booking-choice"
          icon={<ListChecks size={18} aria-hidden />}
          title="What they chose"
          description={row.flow.payment ? 'Paid online, on your website.' : 'Booked on your website, without payment.'}
        >
          <dl className="grid gap-x-4 gap-y-2 text-sm sm:grid-cols-[10rem_minmax(0,1fr)]">
            <Pair label="Service">{row.service_name ?? '—'}</Pair>
            <Pair label="Plan">{row.plan_name ?? '—'}</Pair>
            <Pair label="Case">{row.case_id ? link(`/cases/${row.case_id}`, 'Open the case') : '—'}</Pair>
            <Pair label="Request">
              {row.request_id ? link('/cases?queue=requests', 'In your requests') : '—'}
            </Pair>
          </dl>
        </RecordSection>

        <RecordSection
          id="booking-payment"
          icon={<IndianRupee size={18} aria-hidden />}
          title="Invoice and payment"
          description="The invoice the booking issued, and what is still owed on it."
        >
          <dl className="grid gap-x-4 gap-y-2 text-sm sm:grid-cols-[10rem_minmax(0,1fr)]" data-payment="">
            <Pair label="Invoice">
              {row.invoice_id
                ? link(`/billing/invoices/${row.invoice_id}`, row.invoice_number ?? 'Open the invoice')
                : 'None issued'}
            </Pair>
            <Pair label="Invoice status">
              {row.payment.invoice_status ? (
                <StatusBadge kind="invoice" status={row.payment.invoice_status} />
              ) : (
                '—'
              )}
            </Pair>
            <Pair label="Total">
              <Money value={row.payment.grand_total} />
            </Pair>
            <Pair label="Outstanding">
              <Money value={row.payment.outstanding} />
            </Pair>
          </dl>
        </RecordSection>

        <RecordSection
          id="booking-timeline"
          icon={<CalendarClock size={18} aria-hidden />}
          title="Timeline"
          description="When each thing happened."
        >
          <ol className="space-y-2 text-sm" data-timeline="">
            {(
              [
                ['Started', row.created_at],
                ['Submitted', row.submitted_at],
                ['Confirmed', row.confirmed_at],
                ['Closed', row.closed_at],
              ] as const
            )
              .filter(([, at]) => at)
              .map(([label, at]) => (
                <li key={label} className="flex flex-wrap justify-between gap-2">
                  <span className="text-foreground">{label}</span>
                  <span className="text-muted-foreground">{formatDateTime(at as string)}</span>
                </li>
              ))}
          </ol>
        </RecordSection>
      </div>

      {/* w27c — C25, ADR-0053 §5: what the visitor sent with it. */}
      <BookingDocuments bookingId={row.id} status={row.status} />
    </RecordPage>
  )
}

function Pair({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </>
  )
}
